import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { regenerateWorksheet, markWorksheetComplete } from '../../api/worksheets';
import { useDelight } from '../../contexts/DelightContext';
import Button from '../../components/common/Button';
import s from './WorksheetPreviewPage.module.css';

function useTypeLabels() {
  const { t } = useTranslation();
  return {
    stroke_practice: t('workbook.writingPractice'),
    heuristic: t('workbook.mnemonic'),
    mixed: t('workbook.mixedPractice'),
  };
}

export default function WorksheetPreviewContent({ worksheet, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueDelight } = useDelight();
  const TYPE_LABELS = useTypeLabels();
  const [localWorksheet, setLocalWorksheet] = useState(worksheet);
  const [regenerating, setRegenerating] = useState(false);
  const [pdfError, setPdfError] = useState(worksheet?.pdf_error || null);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(!!worksheet?.completed_at);
  const [completeError, setCompleteError] = useState(null);

  function handleDownload() {
    if (!localWorksheet?.pdf_url) return;
    window.open(localWorksheet.pdf_url, '_blank', 'noopener,noreferrer');
  }

  async function handleMarkComplete() {
    if (!localWorksheet?.id || completing || completed) return;
    setCompleting(true);
    setCompleteError(null);
    try {
      const result = await markWorksheetComplete(localWorksheet.id);
      if (result?.progression) {
        enqueueDelight(result.progression);
      }
      setLocalWorksheet((prev) => ({ ...prev, ...result }));
      setCompleted(true);
      queryClient.invalidateQueries({ queryKey: ['childWorksheets'] });
    } catch (err) {
      setCompleteError(err?.message || 'Failed to mark as complete. Please try again.');
    } finally {
      setCompleting(false);
    }
  }

  async function handleRegenerate() {
    if (!localWorksheet?.id) return;

    setRegenerating(true);
    setPdfError(null);
    try {
      const result = await regenerateWorksheet(localWorksheet.id);
      setLocalWorksheet((prev) => ({ ...prev, ...result }));
      if (result.pdf_error) setPdfError(result.pdf_error);
    } catch (err) {
      setPdfError(err.message || 'Regeneration failed. Please try again.');
    } finally {
      setRegenerating(false);
    }
  }

  if (!localWorksheet) return null;

  const characters = localWorksheet.characters || [];
  const hasPdf = !!localWorksheet.pdf_url;
  const typeLabel = TYPE_LABELS[localWorksheet.worksheet_type] || t('workbook.practice');
  const charSummary = characters
    .map((c) => `${c.char || c.simplified_char} (${c.meaning || c.definition_en || ''})`)
    .join(' · ');

  return (
    <div className={s.content}>
      {/* Title */}
      <h1 className={s.title}>
        {t('worksheetPreview.typeAndCount', { type: typeLabel, count: characters.length })}
      </h1>

      {charSummary && (
        <p className={s.charSummary}>{charSummary}</p>
      )}

      {/* Characters in this worksheet */}
      <div className={s.charsSection}>
        <div className={s.charsCard}>
          <p className={s.charsLabel}>{t('worksheetPreview.charsInWorksheet')}</p>
          <div className={s.tilesGrid}>
            {characters.map((char, i) => (
              <div key={char.id || char.char || i} className={s.tile}>
                <span className={s.tileChar}>{char.char || char.simplified_char}</span>
                {(char.pinyin || char.definition_en || char.meaning) && (
                  <span className={s.tilePinyin}>{char.pinyin}</span>
                )}
                {(char.meaning || char.definition_en) && (
                  <span className={s.tileMeaning}>{char.meaning || char.definition_en}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className={s.actionArea}>
        {hasPdf ? (
          <Button className={s.fullWidthBtn} onClick={handleDownload}>
            {t('worksheetPreview.downloadPdf')}
          </Button>
        ) : (
          <div className={s.pdfNotReady}>
            <p className={s.pdfNotReadyText}>
              {pdfError
                ? t('worksheetPreview.pdfError', { error: pdfError })
                : t('worksheetPreview.pdfExpired')}
            </p>
            <button
              type="button"
              className={s.regenerateButton}
              onClick={handleRegenerate}
              disabled={regenerating}
            >
              {regenerating ? t('worksheetPreview.generatingPdf') : t('worksheetPreview.regenerate')}
            </button>
          </div>
        )}

        <Button
          variant="secondary"
          className={s.fullWidthBtn}
          onClick={handleMarkComplete}
          disabled={completing || completed}
        >
          {completed
            ? '✓ Completed'
            : completing
              ? 'Marking complete…'
              : 'Mark as complete'}
        </Button>
        {completeError && (
          <p role="alert" className={s.errorAlert}>
            {completeError}
          </p>
        )}
      </div>
    </div>
  );
}
